import { Alert, App as AntApp, Button, ConfigProvider, Layout, Menu, Result, Select, Space, Spin, type ThemeConfig,
  Typography } from "antd";
import trTR from "antd/locale/tr_TR";
import { type ReactNode, useEffect, useState } from "react";
import { dateTime } from "./format";
import { LoginPage } from "./LoginPage";
import { AuditPage } from "./pages/AuditPage";
import { CatalogPage } from "./pages/CatalogPage";
import { DeskPage } from "./pages/DeskPage";
import { PersonaPage } from "./pages/PersonaPage";
import { PoliciesPage } from "./pages/PoliciesPage";
import { PrivacyApprovalsPage, PrivacyPage } from "./pages/PrivacyPage";
import { ProactivePage } from "./pages/ProactivePage";
import { ReportsPage } from "./pages/ReportsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { SupportPage } from "./pages/SupportPage";
import { type Me, type Role, useSession } from "./session";

interface Area {
  key: string;
  label: string;
  roles: readonly Role[];
  /** Aktif bir mağaza seçilmiş olmalı mı. */
  tenant: boolean;
  render: () => ReactNode;
}


/** Console çalışma alanları (v0.9 §7.2). Yetki her istekte backend'de denetlenir; menü yalnızca yönlendirir. */
const AREAS: readonly Area[] = [
  { key: "settings", label: "Mağaza ayarları", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <SettingsPage /> },
  { key: "persona", label: "Persona", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <PersonaPage /> },
  { key: "policies", label: "Politikalar", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <PoliciesPage /> },
  { key: "proactive", label: "Proaktif mesajlar", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <ProactivePage /> },
  { key: "privacy", label: "Gizlilik ve KVKK", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <PrivacyPage /> },
  { key: "catalog", label: "Katalog", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <CatalogPage /> },
  { key: "reports", label: "Raporlar", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <ReportsPage /> },
  { key: "desk", label: "Satış Masası", roles: ["STORE_ADMIN", "SALES_REP"], tenant: true, render: () => <DeskPage /> },
  { key: "audit", label: "Audit", roles: ["STORE_ADMIN", "TENANT_AUDITOR"], tenant: true, render: () => <AuditPage /> },
  { key: "support", label: "Destek oturumu", roles: ["PLATFORM_OPS"], tenant: false, render: () => <SupportPage /> },
  { key: "privacy-approvals", label: "Gizlilik onayları", roles: ["PLATFORM_OPS"], tenant: false, render: () => <PrivacyApprovalsPage /> },
];

/**
 * WCAG 2.1 AA kontrastı (paket 4.3). Ant Design varsayılanlarında birincil mavi (#1677ff) beyaz yazıyla 4,1:1,
 * ikincil metin (%45 siyah) beyaz zeminde 3,4:1, tehlike kırmızısı (#ff4d4f) 3,3:1 kalıyor; hepsi 4,5:1'in altında.
 * Birincil renk ve tehlike rengi bir ton koyu, ikincil metin %65 siyah. Etiket renkleri `a11y.css` içinde.
 */
export const THEME: ThemeConfig = {
  token: {
    colorPrimary: "#0958d9",
    colorLink: "#0958d9",
    colorError: "#cf1322",
    colorTextSecondary: "rgba(0, 0, 0, 0.65)",
    colorTextDescription: "rgba(0, 0, 0, 0.65)",
    colorTextTertiary: "rgba(0, 0, 0, 0.6)",
  },
};

export function visibleAreas(roles: readonly Role[], hasTenant = true): readonly Area[] {
  return AREAS.filter((area) => area.roles.some((role) => roles.includes(role)) && (hasTenant || !area.tenant));
}

function currentHash(): string {
  return window.location.hash.replace(/^#\/?/, "");
}

function useHashRoute(): [string, (key: string) => void] {
  const [route, setRoute] = useState(currentHash);
  useEffect(() => {
    const onChange = () => setRoute(currentHash());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return [route, (key) => {
    window.location.hash = `/${key}`;
    setRoute(key);
  }];
}

function TenantPicker({ me }: { me: Me }) {
  const selectTenant = useSession((s) => s.selectTenant);
  return (
    <Result status="info" title="Bir mağaza seçin" extra={
      <Select style={{ minWidth: 280 }} placeholder="Mağaza" onChange={(id: string) => void selectTenant(id)}
        options={me.tenants.map((t) => ({ value: t.tenantId, label: t.displayName || t.tenantId }))} />
    } />
  );
}

function Shell({ me }: { me: Me }) {
  const logout = useSession((s) => s.logout);
  const selectTenant = useSession((s) => s.selectTenant);
  const [route, navigate] = useHashRoute();
  const areas = visibleAreas(me.roles, me.activeTenant !== null);
  const area = areas.find((a) => a.key === route) ?? areas[0];
  const activeName = me.tenants.find((t) => t.tenantId === me.activeTenant)?.displayName ?? me.activeTenant;
  const needsTenant = !me.activeTenant && !me.platformOps && me.tenants.length > 0;

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Layout.Header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Typography.Title level={4} style={{ color: "#fff", margin: 0 }}>SalesOS Console</Typography.Title>
        <Space>
          {me.tenants.length > 1 && me.activeTenant && (
            <Select size="small" value={me.activeTenant} style={{ minWidth: 200 }} aria-label="Aktif mağaza"
              onChange={(id: string) => void selectTenant(id)}
              options={me.tenants.map((t) => ({ value: t.tenantId, label: t.displayName || t.tenantId }))} />
          )}
          {me.tenants.length <= 1 && activeName && <Typography.Text style={{ color: "#fff" }}>{activeName}</Typography.Text>}
          <Typography.Text style={{ color: "rgba(255,255,255,0.75)" }}>{me.email}</Typography.Text>
          <Button size="small" onClick={() => void logout()}>Çıkış</Button>
        </Space>
      </Layout.Header>
      {me.supportUntil && (
        <Alert type="warning" banner showIcon
          title={`Destek oturumu: ${activeName ?? ""} mağazasını salt okunur görüyorsunuz (bitiş ${dateTime(me.supportUntil)}).`} />
      )}
      {needsTenant ? <TenantPicker me={me} /> : (
        <Layout>
          <Layout.Sider width={220} theme="light">
            <Menu mode="inline" selectedKeys={area ? [area.key] : []} onClick={({ key }) => navigate(key)}
              items={areas.map(({ key, label }) => ({ key, label }))} />
          </Layout.Sider>
          <Layout.Content style={{ padding: 24 }}>
            {area ? area.render() : (
              <Result status="403" title="Erişebileceğiniz bir alan yok" subTitle="Yetki için mağaza yöneticinize başvurun." />
            )}
          </Layout.Content>
        </Layout>
      )}
    </Layout>
  );
}

export function App() {
  const me = useSession((s) => s.me);
  const loaded = useSession((s) => s.loaded);
  const refresh = useSession((s) => s.refresh);

  useEffect(() => {
    if (!loaded) {
      refresh().catch(() => undefined);
    }
  }, [loaded, refresh]);

  return (
    <ConfigProvider locale={trTR} theme={THEME}>
      <AntApp>
        {!loaded ? <Spin style={{ display: "block", marginTop: 96 }} />
          : me ? <Shell me={me} />
            : (
              <Layout style={{ minHeight: "100vh" }}>
                <Layout.Content><LoginPage /></Layout.Content>
              </Layout>
            )}
      </AntApp>
    </ConfigProvider>
  );
}
